import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Trans } from '@lingui/react/macro'
import * as Dialog from '@radix-ui/react-dialog'

import { useScrollIntoVisualViewport } from '@jotta/hooks'
import { Button } from '@jotta/ui/Button'
import SvgCheck from '@jotta/ui/SvgCheck'
import SvgX from '@jotta/ui/SvgX'
import { useHideFabOnMobile } from '@jotta/ui/useHideFab'
import { useHideModuleNavOnMobile } from '@jotta/ui/useHideModuleNav'

import { useRenamePathSubmitHandler } from './hooks/useRenamePath'
import type { PathItemStore } from './PathItemStore'
import { useSelectNamePart } from './useSelectNamePart'

export interface PathItemRowRenameProps {
  item: PathItemStore
}

export const PathItemRowRename = observer<PathItemRowRenameProps>(
  function PathItemRowRename({ item }) {
    const open = item.showRenameInput
    const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null)

    useHideFabOnMobile(Boolean(inputEl))
    useHideModuleNavOnMobile(Boolean(inputEl))
    useSelectNamePart(inputEl)

    const virtualRowHeight = inputEl
      ? parseInt(
          getComputedStyle(inputEl).getPropertyValue('--virtual-row-height'),
          10,
        )
      : 0
    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-height',
        ),
        10,
      ) || 0

    useScrollIntoVisualViewport(inputEl, {
      offsetTop: virtualRowHeight + headerHeight + 8,
      offsetBottom: 8,
    })

    const newName = item.model.name
    const { onCancel, onSubmit, errorMessage } = useRenamePathSubmitHandler(
      item,
      newName,
    )

    return (
      <Dialog.Root
        open={open}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            onCancel()
          }
        }}
      >
        <Dialog.Overlay>
          <Dialog.Content data-rename className="h-full">
            <form
              onSubmit={onSubmit}
              className="grid h-full grid-cols-[1fr_max-content_max-content] items-center gap-2"
            >
              <input
                ref={setInputEl}
                data-isfile={item.isFile}
                type="text"
                className="input"
                value={newName}
                aria-invalid={Boolean(errorMessage)}
                onChange={item.onNameInputChange}
              />
              <Button
                className="btn btn-primary"
                disabled={Boolean(errorMessage)}
                icon={SvgCheck}
              >
                <Trans>Ok</Trans>
              </Button>
              <Dialog.Close asChild>
                <Button className="btn btn-outline" icon={SvgX}>
                  <Trans>Cancel</Trans>
                </Button>
              </Dialog.Close>
            </form>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Root>
    )
  },
)
